import React from 'react';
import dayjs from 'dayjs';
import { CellState } from '../types/workingHours';

type Props = {
    coachId: string;
    date: string;
    state: CellState;
    selected: boolean;
    onMouseDown: (e: React.MouseEvent) => void;
    onMouseEnter: (e: React.MouseEvent) => void;
    onClick: (e: React.MouseEvent) => void;
};

const formatHourLabel = (iso: string): string => {
    if (!iso) return '';
    return dayjs(iso).format('HH');
};

const getCellStyle = (state: CellState, selected: boolean): string => {
    const base =
        'h-[40px] min-w-[40px] w-[40px] border-r border-b border-[#E6E8EA] flex items-center justify-center cursor-pointer text-[13px] select-none transition-colors';

    // 선택 링 — IN/OUT 원색과 구분되도록 보라색 사용
    const selectedRing = selected ? 'ring-[3px] ring-inset ring-[#6D5FD8] z-[1]' : '';

    switch (state.type) {
        case 'default-on':
            // 기본 근무 — 옅은 파랑
            return `${base} ${selectedRing} bg-[#D6E4F7] text-[#2A4A80] hover:brightness-95`;
        case 'default-off':
            // 기본 휴무 — 회색
            return `${base} ${selectedRing} bg-[#ECEEF2] text-[#6B7280] hover:brightness-95`;
        case 'include':
            // 추가 근무 (IN) — 옅은 초록
            return `${base} ${selectedRing} bg-[#C3E8CF] text-[#166E3F] font-medium hover:brightness-95`;
        case 'exclude':
            // 휴무 (OUT) — 옅은 빨강
            return `${base} ${selectedRing} bg-[#F8C8CC] text-[#8B1B2A] font-medium hover:brightness-95`;
    }
};

const renderCellContent = (state: CellState): React.ReactNode => {
    switch (state.type) {
        case 'default-on':
            return <span className="opacity-0 group-hover:opacity-100 text-[#6B7280]">■</span>;
        case 'default-off':
            return null;
        case 'include': {
            if (!state.startTime || !state.endTime) return '?';
            const s = formatHourLabel(state.startTime);
            const e = formatHourLabel(state.endTime);
            return (
                <span className="text-[13px] leading-none">
                    {s}-{e}
                </span>
            );
        }
        case 'exclude':
            // 배경 색만으로 구분 — 텍스트/아이콘 없음
            return null;
    }
};

const getTooltip = (state: CellState, date: string): string => {
    const d = dayjs(date).format('YYYY.MM.DD (dd)');
    switch (state.type) {
        case 'default-on':
            return `${d} · 기본 근무`;
        case 'default-off':
            return `${d} · 기본 휴무`;
        case 'include': {
            const s = state.startTime ? dayjs(state.startTime).format('HH:mm') : '';
            const e = state.endTime ? dayjs(state.endTime).format('HH:mm') : '';
            const time = s && e ? ` ${s} ~ ${e}` : ' (시간 미입력)';
            return `${d} · 추가 근무(IN)${time}${state.memo ? `\n메모: ${state.memo}` : ''}`;
        }
        case 'exclude':
            return `${d} · 근무 제외(OUT)${state.memo ? `\n메모: ${state.memo}` : ''}`;
    }
};

export const WorkGridCell = React.memo(
    ({ coachId, date, state, selected, onMouseDown, onMouseEnter, onClick }: Props) => {
        return (
            <div
                data-coach-id={coachId}
                data-date={date}
                className={`${getCellStyle(state, selected)} group`}
                title={getTooltip(state, date)}
                onMouseDown={onMouseDown}
                onMouseEnter={onMouseEnter}
                onClick={onClick}
            >
                {renderCellContent(state)}
            </div>
        );
    },
);

WorkGridCell.displayName = 'WorkGridCell';
